(() => {
  const N = window.Northyard;
  const config = document.querySelector('[data-commerce-resources]');
  if (!N || !config || N.commerceLoaderReady) return;
  N.commerceLoaderReady = true;
  const resources = JSON.parse(config.textContent);
  const textDecoder = document.createElement('textarea');
  for (const group of ['labels', 'cartLabels']) {
    for (const [key, value] of Object.entries(resources[group])) {
      textDecoder.innerHTML = String(value).replaceAll('<', '&lt;').replaceAll('>', '&gt;');
      resources[group][key] = textDecoder.value;
    }
  }
  N.resourceLabels = resources.labels;
  N.cartLabels = resources.cartLabels;
  const assets = new Map();
  const features = new Map();
  const versions = new WeakMap();
  const prepared = new WeakSet();
  let cartRead = null;
  let cartSectionRead = null;
  let cartRevision = 0;

  const loadAsset = (url, style = false) => {
    if (assets.has(url)) return assets.get(url);
    if (style && [...document.querySelectorAll('link[rel="stylesheet"]')].some(link => link.href === new URL(url, location.href).href && link.sheet)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const element = document.createElement(style ? 'link' : 'script');
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        element.onload = element.onerror = null;
        if (error) {
          element.remove();
          assets.delete(url);
          reject(error);
        } else resolve();
      };
      element.onload = () => finish();
      element.onerror = () => finish(new Error('Resource unavailable'));
      if (style) { element.rel = 'stylesheet'; element.href = url; }
      else { element.async = true; element.src = url; }
      element.dataset.commerceAsset = '';
      document.head.append(element);
    });
    assets.set(url, promise);
    return promise;
  };
  const productState = () => N.ProductState ? Promise.resolve() : loadAsset(resources.scripts.productState);
  N.ensureCommerce = (feature) => {
    if (features.has(feature)) return features.get(feature);
    const style = resources.styles[feature];
    const dependency = feature === 'quickView' || feature === 'cards' ? productState() : Promise.resolve();
    const script = (feature === 'cards' ? dependency : Promise.resolve()).then(() => {
        const ready = feature === 'quickView' ? N.quickViewReady : feature === 'cart' ? N.cartDrawerReady
          : feature === 'search' ? N.searchReady : N.productCardsReady;
        return ready ? undefined : loadAsset(resources.scripts[feature]);
      });
    const pending = Promise.all([dependency, script, style ? loadAsset(style, true) : Promise.resolve()])
      .catch((error) => { features.delete(feature); throw error; });
    features.set(feature, pending);
    return pending;
  };
  const begin = (dialog, opener) => {
    if (!prepared.has(dialog)) {
      prepared.add(dialog);
      dialog.addEventListener('close', () => {
        if (!dialog.open) versions.set(dialog, (versions.get(dialog) || 0) + 1);
      });
    }
    const version = (versions.get(dialog) || 0) + 1;
    versions.set(dialog, version);
    N.openDialog(dialog, opener);
    return () => dialog.open && versions.get(dialog) === version;
  };
  N.showResourceError = (container, retry, href, label, message = resources.labels.unavailable) => {
    const text = document.createElement('p');
    text.setAttribute('role', 'status');
    text.textContent = message;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'text-link';
    button.textContent = resources.labels.retry;
    button.addEventListener('click', retry);
    const link = document.createElement('a');
    link.className = 'text-link';
    link.href = href;
    link.textContent = label;
    const box = document.createElement('div');
    box.className = 'commerce-feedback';
    box.append(text, button, link);
    container.replaceChildren(box);
  };
  const watchLoading = (dialog, container, active, href, label) => {
    const timer = setTimeout(() => {
      if (!active() || container.hidden) return;
      const text = document.createElement('p');
      text.setAttribute('role', 'status');
      text.textContent = resources.labels.slow || resources.labels.loading;
      const link = document.createElement('a');
      link.className = 'text-link';
      link.href = href;
      link.textContent = label;
      const box = document.createElement('div');
      box.className = 'commerce-feedback';
      box.append(text, link);
      container.replaceChildren(box);
    }, 8000);
    const stop = () => {
      clearTimeout(timer);
      dialog.removeEventListener('close', stop);
      if (active() && container.contains(document.activeElement)) {
        dialog.querySelector('[data-overlay-close]')?.focus({ preventScroll: true });
      }
    };
    dialog.addEventListener('close', stop);
    return stop;
  };
  let sizeChartLoad = null;
  let sizeChartReady = false;
  const cancelSizeChartLoad = () => {
    const request = sizeChartLoad;
    if (!request) return;
    sizeChartLoad = null;
    clearTimeout(request.timer);
    request.origin?.removeEventListener('close', cancelSizeChartLoad);
    request.trigger.replaceChildren(...request.contents);
    for (const [name, value] of Object.entries(request.attributes)) {
      if (value === null) request.trigger.removeAttribute(name);
      else request.trigger.setAttribute(name, value);
    }
    request.status.remove();
  };
  const openSizeChart = async (trigger) => {
    if (sizeChartReady) { N.showSizeChart(trigger); return; }
    if (sizeChartLoad?.trigger === trigger && !sizeChartLoad.failed) return;
    cancelSizeChartLoad();
    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    trigger.after(status);
    const request = {
      trigger, status, origin: trigger.closest('dialog'), source: trigger.dataset.sizeChartUrl,
      page: location.href, contents: [...trigger.childNodes],
      attributes: Object.fromEntries(['aria-busy', 'aria-disabled', 'aria-label'].map(name => [name, trigger.getAttribute(name)]))
    };
    sizeChartLoad = request;
    request.origin?.addEventListener('close', cancelSizeChartLoad);
    const active = () => sizeChartLoad === request && trigger.isConnected
      && (!request.origin || request.origin.open) && location.href === request.page
      && trigger.dataset.sizeChartUrl === request.source
      && (Array.from(document.querySelectorAll('dialog[open]')).at(-1) || null) === request.origin;
    const showStatus = (message) => {
      status.className = 'qv-size-chart-status';
      status.textContent = message;
      // Keep the merchant's original image accessible if the optional viewer cannot load.
      if (request.source) {
        const url = new URL(request.source, location.href);
        if (['https:', 'http:'].includes(url.protocol)) {
          const link = document.createElement('a');
          link.href = url.href;
          link.target = '_blank';
          link.rel = 'noopener';
          link.className = 'text-link';
          link.textContent = N.qs('[data-quick-view]')?.dataset.labelSizeGuide || request.contents.map(node => node.textContent).join('');
          status.append(document.createTextNode(' '), link);
        }
      }
    };
    trigger.textContent = resources.labels.loading;
    trigger.setAttribute('aria-busy', 'true');
    trigger.setAttribute('aria-disabled', 'true');
    status.textContent = resources.labels.loading;
    request.timer = setTimeout(() => {
      if (active()) showStatus(resources.labels.slow || resources.labels.loading);
    }, 8000);
    try {
      // Direct PDP visitors have not necessarily loaded Quick View's shared viewer.
      // Wait for both its script and CSS; never eagerly add them to every page.
      await N.ensureCommerce('quickView');
      if (typeof N.showSizeChart !== 'function') throw new Error('Size chart viewer unavailable');
      sizeChartReady = true;
      if (!active()) { if (sizeChartLoad === request) cancelSizeChartLoad(); return; }
      cancelSizeChartLoad();
      N.showSizeChart(trigger);
    } catch {
      if (!active()) { if (sizeChartLoad === request) cancelSizeChartLoad(); return; }
      clearTimeout(request.timer);
      request.failed = true;
      trigger.removeAttribute('aria-busy');
      trigger.removeAttribute('aria-disabled');
      trigger.textContent = resources.labels.retry;
      trigger.setAttribute('aria-label', resources.labels.unavailable + ' ' + resources.labels.retry);
      showStatus(resources.labels.unavailable);
    }
  };
  window.addEventListener('pagehide', cancelSizeChartLoad);
  N.openQuickView = async (productUrl, options = {}) => {
    const dialog = N.qs('[data-quick-view]');
    if (!dialog) { location.assign(productUrl); return; }
    const active = begin(dialog, options.opener);
    N.promoteQuickViewWarm?.(productUrl);
    const loading = N.qs('[data-quick-view-loading]', dialog);
    loading.hidden = false;
    loading.textContent = dialog.dataset.labelLoading;
    N.qs('[data-quick-view-content]', dialog).hidden = true;
    const stopWaiting = watchLoading(dialog, loading, active, productUrl, resources.labels.details);
    // The customer has clicked: read fresh commerce while the QV bundle/CSS loads.
    // Attach a rejection handler immediately, even if the dialog closes before await.
    const openingPayload = productState().then(() => N.ProductState.request(productUrl, { variantId: options.variantId, fresh: true }));
    openingPayload.catch(() => {});
    try {
      await N.ensureCommerce('quickView');
      stopWaiting();
      if (active()) await N.showQuickView(productUrl, { ...options, openingPayload, isCurrent: active });
    } catch {
      stopWaiting();
      if (active()) N.showResourceError(loading, () => N.openQuickView(productUrl, options), productUrl, resources.labels.details);
    } finally {
      stopWaiting();
    }
  };

  N.updateCartCounts = (cart) => {
    if (typeof cart.item_count !== 'number') return;
    N.qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    const drawer = N.qs('[data-cart-drawer]');
    const count = N.qs('[data-cart-drawer-count]');
    if (count) count.textContent = '(' + cart.item_count + ')';
    if (drawer) N.qsa('[data-cart-open]').forEach((button) => {
      button.setAttribute('aria-label', drawer.dataset.labelOpen.replace('__COUNT__', cart.item_count));
    });
  };
  N.cartRequest = async (path, options = {}) => {
    const mutation = options.method && options.method !== 'GET';
    if (mutation && (N.cartMutationPending || (N.cartEditPending && !options.cartEdit))) throw new Error(N.cartLabels.busy);
    if (mutation && N.cartUncertain) throw Object.assign(new Error(N.cartLabels.uncertain), { needsReview: true });
    if (mutation) {
      cartRead = null; cartSectionRead = null; cartRevision += 1;
      N.cartMutationPending = true;
      document.dispatchEvent(new CustomEvent('northyard:cart-busy'));
    }
    const revision = cartRevision;
    const { cartEdit, ...requestOptions } = options;
    let response;
    try {
      response = await fetch(N.path(path), {
        ...requestOptions, cache: 'no-store', signal: options.signal || AbortSignal.timeout(15000),
        headers: Object.assign({ Accept: 'application/json', 'Content-Type': 'application/json' }, options.headers || {})
      });
      const data = await response.json();
      if (!response.ok) throw Object.assign(new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : N.cartLabels.update_failed), { status: response.status });
      if (!data || typeof data !== 'object' || (!Array.isArray(data.items) && typeof data.item_count !== 'number')) throw new Error(N.cartLabels.uncertain);
      if (revision === cartRevision) N.updateCartCounts(data);
      return data;
    } catch (error) {
      if (mutation) {
        N.cartUncertain = true;
        const message = response && response.status >= 400 && response.status < 500 ? error.message : N.cartLabels.uncertain;
        throw Object.assign(new Error(message), { needsReview: true, status: response?.status, cause: error });
      }
      throw error;
    } finally {
      if (mutation) {
        N.cartMutationPending = false;
        document.dispatchEvent(new CustomEvent('northyard:cart-busy'));
      }
    }
  };
  const readCart = () => {
    if (!cartRead) {
      const pending = N.cartRequest('/cart.js', { method: 'GET', signal: AbortSignal.timeout(12000) });
      cartRead = pending;
      pending.finally(() => { if (cartRead === pending) cartRead = null; }).catch(() => {});
    }
    return cartRead;
  };
  N.readCart = readCart;
  N.readCartSection = ({ fresh = false } = {}) => {
    if (fresh) cartSectionRead = null;
    if (!cartSectionRead) {
      const pending = fetch(N.path('/cart') + '?section_id=cart-state', { cache: 'no-store', signal: AbortSignal.timeout(12000) })
        .then(response => { if (!response.ok) throw new Error(N.cartLabels.refresh_failed); return response.text(); });
      cartSectionRead = pending;
      pending.finally(() => { if (cartSectionRead === pending) cartSectionRead = null; }).catch(() => {});
    }
    return cartSectionRead;
  };
  N.openCart = async (opener) => {
    const dialog = N.qs('[data-cart-drawer]');
    if (!dialog) { location.assign(N.path('/cart')); return; }
    const active = begin(dialog, opener);
    const body = N.qs('[data-cart-drawer-body]', dialog);
    body.textContent = dialog.dataset.labelLoading;
    body.setAttribute('aria-busy', 'true');
    const stopWaiting = watchLoading(dialog, body, active, N.path('/cart'), resources.labels.cart);
    try {
      const [, markup] = await Promise.all([
        N.ensureCommerce('cart'),
        N.readCartSection()
      ]);
      stopWaiting();
      if (active()) {
        N.renderCartHTML(markup);
        if (!N.cartMutationPending && !N.cartEditPending) N.cartUncertain = false;
        N.syncCartControls();
      }
    } catch {
      stopWaiting();
      if (active()) N.showResourceError(body, () => N.openCart(opener), N.path('/cart'), resources.labels.cart);
    } finally {
      stopWaiting();
      if (active()) body.removeAttribute('aria-busy');
    }
  };
  N.openSearch = async (opener) => {
    const dialog = N.qs('[data-search-panel]');
    if (!dialog) { location.assign(N.path('/search')); return; }
    const active = begin(dialog, opener);
    N.qs('[data-predictive-search-input]', dialog)?.focus({ preventScroll: true });
    const results = N.qs('[data-predictive-search-results]', dialog);
    const trending = N.qs('[data-search-trending]', dialog);
    if (!N.searchReady) { trending.hidden = true; results.textContent = resources.labels.loading; }
    const stopWaiting = watchLoading(dialog, results, active, N.path('/search'), resources.labels.search);
    try {
      await N.ensureCommerce('search');
      stopWaiting();
      if (active()) N.startSearch();
    } catch {
      stopWaiting();
      if (active()) {
        trending.hidden = true;
        N.showResourceError(results, () => N.openSearch(opener), N.path('/search'), resources.labels.search);
      }
    } finally {
      stopWaiting();
    }
  };
  N.initProductCards = (root = document) => {
    if (!root.querySelector('[data-product-card]')) return;
    N.ensureCommerce('cards').then(() => {
      if (root.isConnected) N.initProductCards(root);
    }).catch(() => {});
  };
  document.addEventListener('shopify:section:load', (event) => N.initProductCards(event.target));
  document.addEventListener('DOMContentLoaded', () => {
    N.initProductCards();
    if (document.querySelector('[data-cart-page]')) N.ensureCommerce('cart').catch(() => {});
  });

  const featureFor = (target) => target.closest('[data-quick-view-trigger]') ? 'quickView'
    : target.closest('[data-search-open]') ? 'search' : target.closest('[data-cart-open]') ? 'cart' : null;
  let intentTimer = 0;
  let intentTrigger = null;
  const clearIntent = () => {
    clearTimeout(intentTimer);
    intentTrigger = null;
    N.cancelQuickViewWarm?.();
  };
  const warm = (event) => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    if (navigator.connection?.saveData) return;
    const feature = featureFor(event.target);
    if (feature) N.ensureCommerce(feature).catch(() => {});
    if (feature !== 'quickView' || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    const trigger = event.target.closest('[data-quick-view-trigger]');
    if (intentTrigger === trigger) return;
    clearIntent();
    intentTrigger = trigger;
    intentTimer = setTimeout(async () => {
      try {
        await N.ensureCommerce('quickView');
        if (intentTrigger !== trigger || !trigger.isConnected || N.qs('[data-quick-view]')?.open) return;
        const card = trigger.closest('[data-product-card]');
        if (card?.getAttribute('aria-busy') === 'true') return;
        const url = (card ? N.qs('[data-card-product-link]', card)?.href : null) || trigger.dataset.productUrl;
        await N.warmQuickView?.(url);
      } catch { /* Speculation never replaces the click's retryable request. */ }
    }, 120);
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);
  ['pointerout', 'focusout'].forEach(type => document.addEventListener(type, (event) => {
    if (intentTrigger?.contains(event.target) && !intentTrigger.contains(event.relatedTarget)) clearIntent();
  }, { passive: true }));
  document.addEventListener('visibilitychange', () => { if (document.hidden) clearIntent(); });
  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const chartTrigger = event.target.closest('[data-size-guide-open]');
    if (chartTrigger && !event.defaultPrevented) {
      event.preventDefault();
      openSizeChart(chartTrigger);
      return;
    }
    const feature = featureFor(event.target);
    if (!feature) return;
    event.preventDefault();
    if (feature === 'quickView') {
      const trigger = event.target.closest('[data-quick-view-trigger]');
      const card = trigger.closest('[data-product-card]');
      const selectedUrl = card ? N.qs('[data-card-product-link]', card)?.href : null;
      N.openQuickView(selectedUrl || trigger.dataset.productUrl, { opener: trigger, color: card?.dataset.activeColor || '' });
    } else if (feature === 'cart') N.openCart(event.target.closest('[data-cart-open]'));
    else N.openSearch(event.target.closest('[data-search-open]'));
  });
})();
